import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  Clapperboard,
  CodeXml,
  Palette,
  PenLine,
  Building2,
  Copyright,
  FilePen,
  FileText,
  Gavel,
  Ghost,
  HandCoins,
  IndianRupee,
  Landmark,
  Lock,
  Mail,
  MessageCircle,
  Percent,
  Receipt,
  Ruler,
  Scale,
  Stamp,
  type LucideIcon,
} from 'lucide-react';
import { GUIDES, type Guide, type GuideCategory } from '../../seo/guides';
import SectionDivider from './SectionDivider';

/**
 * Guides — real internal links to the 3 long-form guide pages already
 * live at /guides/:slug (src/seo/guides.ts is the content source, also
 * driving the Article + FAQPage schema on those pages). Plain <Link>
 * elements on purpose, not onClick+navigate — this section's whole point
 * is internal linking, which needs a real crawlable <a href>, not a JS
 * click handler.
 *
 * Each card carries its own topic icon (a gavel for disputes, a receipt for
 * GST, and so on) tinted by category. There are 17 guides but only a handful
 * of Hoot mascot poses, so the mascot repeated on almost every card.
 */

type Variant = 'dispute' | 'contract' | 'record' | 'tax';

// Variant (colour) follows the guide's category, so a newly added guide always
// has a card style. (It used to be a per-slug map, and a guide missing from it
// crashed the whole section.)
const VARIANT_BY_CATEGORY: Record<GuideCategory, Variant> = {
  'Getting paid': 'dispute',
  Contracts: 'contract',
  'Tax and invoicing': 'tax',
  'Evidence and law': 'record',
};

// Each guide gets its own topic icon so no two cards look the same; the
// category supplies the colour. Unlisted guides fall back to a category icon.
const ICON_BY_SLUG: Record<string, LucideIcon> = {
  'freelance-contract-india': FilePen,
  'freelance-contract-template-india': FileText,
  'client-not-paying-freelancer-india': IndianRupee,
  'raise-dispute-against-non-paying-client-india': Gavel,
  'legal-notice-unpaid-freelance-work-india': Mail,
  'msefc-msme-samadhaan-freelancers-india': Landmark,
  'whatsapp-agreement-legally-valid-india': MessageCircle,
  'udyam-registration-for-freelancers-india': Building2,
  'gst-for-freelancers-india': Receipt,
  'tds-on-freelancer-payments-india': Percent,
  'client-ghosted-freelancer-india': Ghost,
  'scope-creep-freelance-contract-clause': Ruler,
  'who-owns-freelance-work-copyright-india': Copyright,
  'summary-suit-order-37-recover-money-india': Scale,
  'advance-payment-freelancers-india': HandCoins,
  'nda-for-freelancers-india': Lock,
  'electronic-signature-legal-validity-india': Stamp,
  'freelance-web-developer-contract-india': CodeXml,
  'graphic-designer-contract-india': Palette,
  'video-editor-contract-india': Clapperboard,
  'content-writer-contract-india': PenLine,
};

const ICON_BY_CATEGORY: Record<GuideCategory, LucideIcon> = {
  'Getting paid': IndianRupee,
  Contracts: FileText,
  'Tax and invoicing': Receipt,
  'Evidence and law': Scale,
};

/** Shown on the homepage; the rest live on /guides. */
const FEATURED_SLUGS = [
  'freelance-contract-india',
  'freelance-web-developer-contract-india',
  'client-not-paying-freelancer-india',
  'raise-dispute-against-non-paying-client-india',
  'legal-notice-unpaid-freelance-work-india',
  'freelance-contract-template-india',
];

const VARIANT_STYLES: Record<Variant, { tint: string; baseBorder: string; hoverBorder: string; icon: string; tile: string }> = {
  dispute: {
    tint: 'from-amber-400/[0.12]',
    baseBorder: 'border-amber-400/15',
    hoverBorder: 'hover:border-amber-400/40 hover:bg-amber-400/[0.03]',
    icon: 'text-amber-300',
    tile: 'border-amber-400/25 bg-amber-400/[0.08]',
  },
  contract: {
    tint: 'from-[#3cb44f]/[0.12]',
    baseBorder: 'border-[#3cb44f]/15',
    hoverBorder: 'hover:border-[#3cb44f]/40 hover:bg-[#3cb44f]/[0.03]',
    icon: 'text-[#5cd06f]',
    tile: 'border-[#3cb44f]/25 bg-[#3cb44f]/[0.08]',
  },
  record: {
    tint: 'from-teal-400/[0.12]',
    baseBorder: 'border-teal-400/15',
    hoverBorder: 'hover:border-teal-400/40 hover:bg-teal-400/[0.03]',
    icon: 'text-teal-300',
    tile: 'border-teal-400/25 bg-teal-400/[0.08]',
  },
  tax: {
    tint: 'from-violet-400/[0.12]',
    baseBorder: 'border-violet-400/15',
    hoverBorder: 'hover:border-violet-400/40 hover:bg-violet-400/[0.03]',
    icon: 'text-violet-300',
    tile: 'border-violet-400/25 bg-violet-400/[0.08]',
  },
};

function GuideArt({ guide, variant }: { guide: Guide; variant: Variant }) {
  const styles = VARIANT_STYLES[variant];
  const Icon = ICON_BY_SLUG[guide.slug] ?? ICON_BY_CATEGORY[guide.category];

  return (
    <div className={`relative h-40 md:h-44 flex items-center justify-center overflow-hidden bg-gradient-to-b ${styles.tint} to-transparent`}>
      {/* Oversized, faint copy of the icon for texture behind the tile. */}
      <Icon
        aria-hidden="true"
        strokeWidth={1}
        className={`absolute -right-6 -bottom-8 w-44 h-44 opacity-[0.07] ${styles.icon}`}
      />
      <motion.div
        className={`relative w-20 h-20 md:w-24 md:h-24 rounded-3xl border flex items-center justify-center ${styles.tile}`}
        whileHover={{ scale: 1.06, rotate: -3 }}
        transition={{ type: 'spring', stiffness: 260, damping: 16 }}
      >
        <Icon aria-hidden="true" strokeWidth={1.6} className={`w-10 h-10 md:w-11 md:h-11 ${styles.icon}`} />
      </motion.div>
      <span className="absolute left-5 top-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
        {guide.category}
      </span>
    </div>
  );
}

const dateFormatter = new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric' });

/**
 * The card grid itself, exported so GuidesIndex.tsx (the dedicated /guides
 * listing page) can render the exact same cards instead of a second,
 * plainer design drifting out of sync with this one.
 */
export function GuideCards({
  className = 'grid grid-cols-1 md:grid-cols-3 gap-5',
  guides = GUIDES,
}: {
  className?: string;
  guides?: Guide[];
}) {
  return (
    <div className={className}>
      {guides.map((guide, i) => {
        const variant = VARIANT_BY_CATEGORY[guide.category] ?? 'contract';
        const styles = VARIANT_STYLES[variant];
        return (
          <motion.div
            key={guide.slug}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
          >
            <Link
              to={`/guides/${guide.slug}`}
              className={`group flex flex-col h-full rounded-3xl border ${styles.baseBorder} bg-white/[0.02] overflow-hidden transition-colors duration-300 ${styles.hoverBorder}`}
            >
              <GuideArt guide={guide} variant={variant} />

              <div className="flex flex-col flex-1 p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span className="font-mono text-[11px] text-white/35 tabular-nums">
                    {dateFormatter.format(new Date(guide.updated))}
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="shrink-0 text-white/30 transition-all duration-300 group-hover:text-[#3cb44f] group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </div>
                <h3 className="font-bricolage font-bold text-[16px] md:text-[17px] text-white leading-snug mb-3 text-balance">
                  {guide.h1}
                </h3>
                <p className="text-[13px] text-white/50 leading-relaxed line-clamp-4">{guide.standfirst}</p>
                <span className="mt-5 text-[13px] font-semibold text-[#3cb44f] inline-flex items-center gap-1.5">
                  Read guide
                  <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function GuidesRevamp() {
  return (
    <>
      <SectionDivider />
      <section className="relative bg-black py-20 md:py-24">
        <div className="px-6 max-w-5xl mx-auto">
          {/* Docket-row family, matching How It Works/Features — this section
            is a grid of three real, citable documents, same as those. */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10 md:mb-12"
          >
            <div className="flex items-center gap-4 mb-5">
              <span className="text-[11px] tracking-[0.3em] text-[#3cb44f]/80 uppercase shrink-0">
                Reading list
              </span>
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[11px] tracking-[0.2em] text-white/30 uppercase shrink-0">{String(GUIDES.length).padStart(2, '0')} guides</span>
            </div>
            <h2 className="max-w-xl font-bricolage font-extrabold text-3xl md:text-5xl text-white leading-[1.1] tracking-tight text-left mb-4">
              The law, <br />explained in{' '}
              <span className="font-instrumentserif italic font-normal text-accent">plain</span> English
            </h2>
            <p className="max-w-xl text-white/55 text-sm md:text-base leading-relaxed text-left">
              Practical guides on the exact situations above — the clauses that matter, the notice that starts
              interest running, GST and TDS, and what actually holds up in court.
            </p>
          </motion.div>

          <GuideCards guides={FEATURED_SLUGS.map((slug) => GUIDES.find((g) => g.slug === slug)).filter((g): g is Guide => Boolean(g))} />

          <div className="mt-10 text-center">
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-[13px] font-semibold text-white/80 transition-colors hover:border-[#3cb44f]/50 hover:text-white"
            >
              Browse all {GUIDES.length} guides
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
